import { useRetainedRealtimeState } from '../../services/firebase/use-retained-realtime-state'
import { useEffect, useRef } from 'react'

import type { RealtimeOverview } from './overview-contracts'
import { parseRealtimeOverview } from './overview-model'
import {
  isPermissionDenied,
  type OverviewRepository,
} from './overview-repository'

export const OVERVIEW_STALE_AFTER_MS = 15 * 60 * 1000

export type OverviewMetricStatus =
  'loading' | 'ready' | 'warming' | 'stale' | 'forbidden' | 'unavailable'

type OverviewMetricState = {
  owner: string | null
  status: OverviewMetricStatus
  snapshot: RealtimeOverview | null
}

function emptyState(owner: string | null): OverviewMetricState {
  return { owner, status: 'loading', snapshot: null }
}

function snapshotStatus(
  snapshot: RealtimeOverview,
  nowMs: number
): OverviewMetricStatus {
  if (snapshot.status === 'stale') return 'stale'
  if (snapshot.status === 'warming' || !snapshot.complete) return 'warming'
  return nowMs - snapshot.updatedAtMs >= OVERVIEW_STALE_AFTER_MS
    ? 'stale'
    : 'ready'
}

export function useOverviewMetrics(
  repository: OverviewRepository,
  tenantId: string | null
) {
  const generation = useRef(0)
  const [state, setState, resume] =
    useRetainedRealtimeState<OverviewMetricState>(
      ['overview', tenantId, 'metrics'],
      emptyState(tenantId),
      (value) => value.snapshot !== null
    )

  useEffect(() => {
    const currentGeneration = ++generation.current
    resume()
    if (!tenantId) return

    const unsubscribe = repository.subscribeOverview(
      tenantId,
      (value) => {
        if (generation.current !== currentGeneration) return
        const snapshot = parseRealtimeOverview(value)
        if (!snapshot) {
          setState({ owner: tenantId, status: 'unavailable', snapshot: null })
          return
        }
        const status = snapshotStatus(snapshot, Date.now())
        setState({ owner: tenantId, status, snapshot })
      },
      (error) => {
        if (generation.current !== currentGeneration) return
        setState({
          owner: tenantId,
          status: isPermissionDenied(error) ? 'forbidden' : 'unavailable',
          snapshot: null,
        })
      }
    )

    return () => {
      generation.current += 1
      unsubscribe()
    }
  }, [repository, tenantId, resume, setState])

  // The staleness clock also runs for a reused snapshot while RTDB reconnects.
  useEffect(() => {
    const snapshot = state.snapshot
    if (!snapshot || snapshotStatus(snapshot, Date.now()) !== 'ready') return
    const timer = setTimeout(
      () => {
        setState((current) =>
          current.snapshot === snapshot
            ? { ...current, status: 'stale' }
            : current
        )
      },
      Math.max(1, snapshot.updatedAtMs + OVERVIEW_STALE_AFTER_MS - Date.now())
    )
    return () => clearTimeout(timer)
  }, [state.snapshot, setState])

  return state.owner === tenantId
    ? state.snapshot
      ? { ...state, status: snapshotStatus(state.snapshot, Date.now()) }
      : state
    : emptyState(tenantId)
}
